import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import marketplaceService from '../../../services/marketplaceService';
import TrustBadge from '../../../components/TrustBadge';
import StarRating from '../../../components/StarRating';

const ActiveListings = ({ onSelectListing }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    collectibleType: '',
    condition: '',
    minPrice: '',
    maxPrice: ''
  });

  useEffect(() => {
    loadListings();
  }, []);

  const loadListings = async () => {
    setLoading(true);
    const { data, error } = await marketplaceService?.getActiveListings(filters);
    if (!error) {
      setListings(data || []);
    }
    setLoading(false);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadListings();
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      collectibleType: '',
      condition: '',
      minPrice: '',
      maxPrice: ''
    });
    setTimeout(() => loadListings(), 100);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
        {[1, 2, 3, 4, 5, 6]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-lg h-96" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Input
            placeholder="Search listings..."
            value={filters?.search}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
          />
          <Select
            value={filters?.collectibleType}
            onChange={(e) => handleFilterChange('collectibleType', e?.target?.value)}
          >
            <option value="">All Types</option>
            <option value="vintage-watches">Vintage Watches</option>
            <option value="rare-coins">Rare Coins</option>
            <option value="sports-cards">Sports Cards</option>
            <option value="art-prints">Art Prints</option>
            <option value="stamps">Stamps</option>
          </Select>
          <Select
            value={filters?.condition}
            onChange={(e) => handleFilterChange('condition', e?.target?.value)}
          >
            <option value="">All Conditions</option>
            <option value="mint">Mint</option>
            <option value="near-mint">Near Mint</option>
            <option value="excellent">Excellent</option>
            <option value="very-good">Very Good</option>
          </Select>
          <Input
            type="number"
            placeholder="Min Price"
            value={filters?.minPrice}
            onChange={(e) => handleFilterChange('minPrice', e?.target?.value)}
          />
          <Input
            type="number"
            placeholder="Max Price"
            value={filters?.maxPrice}
            onChange={(e) => handleFilterChange('maxPrice', e?.target?.value)}
          />
        </div>
        <div className="flex space-x-3 mt-4">
          <Button onClick={applyFilters} iconName="Search" iconPosition="left">
            Apply Filters
          </Button>
          <Button onClick={clearFilters} variant="outline" iconName="X" iconPosition="left">
            Clear
          </Button>
        </div>
      </div>
      {/* Listings Grid */}
      {listings?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No listings found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or check back later</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings?.map(listing => (
            <div
              key={listing?.id}
              className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => onSelectListing(listing)}
            >
              {/* Image */}
              <div className="aspect-square bg-muted relative">
                {listing?.featured_image_url || listing?.collectible?.primary_image_url ? (
                  <img
                    src={listing?.featured_image_url || listing?.collectible?.primary_image_url}
                    alt={listing?.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Image" size={48} className="text-muted-foreground" />
                  </div>
                )}
                {listing?.is_negotiable && (
                  <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-medium">
                    Negotiable
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-1">
                  {listing?.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {listing?.description}
                </p>

                {/* Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Condition:</span>
                    <span className="font-medium text-foreground capitalize">
                      {listing?.condition?.replace('-', ' ')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Seller:</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">
                        {listing?.seller?.full_name}
                      </span>
                      {listing?.seller_reputation?.[0]?.seller_rating > 0 && (
                        <StarRating
                          rating={listing?.seller_reputation?.[0]?.seller_rating || 0}
                          size={11}
                          showValue={true}
                        />
                      )}
                    </div>
                  </div>
                  {/* Trust Badges on card */}
                  {listing?.seller_reputation?.[0]?.total_sales >= 1 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {listing?.seller_reputation?.[0]?.seller_rating >= 4.5 && listing?.seller_reputation?.[0]?.total_sales >= 10 && (
                        <TrustBadge badgeType="top_rated_seller" size="xs" showTooltip={false} />
                      )}
                      {listing?.seller_reputation?.[0]?.total_sales >= 50 && (
                        <TrustBadge badgeType="power_seller" size="xs" showTooltip={false} />
                      )}
                      {listing?.seller_reputation?.[0]?.total_sales >= 1 && listing?.seller_reputation?.[0]?.seller_rating === 0 && (
                        <TrustBadge badgeType="verified_seller" size="xs" showTooltip={false} />
                      )}
                    </div>
                  )}
                </div>

                {/* Price */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <p className="text-2xl font-bold text-primary">
                      ${parseFloat(listing?.asking_price || 0)?.toFixed(2)}
                    </p>
                    {listing?.shipping_cost > 0 && (
                      <p className="text-xs text-muted-foreground">
                        +${parseFloat(listing?.shipping_cost)?.toFixed(2)} shipping
                      </p>
                    )}
                  </div>
                  <Button size="sm" iconName="Eye" iconPosition="left">
                    View Details
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActiveListings;
